import { useEffect } from "react";
import {
  RealtimeKitProvider,
  useRealtimeKitClient,
  useRealtimeKitMeeting,
  useRealtimeKitSelector,
} from "@cloudflare/realtimekit-react";
import { RTK_HIGH_QUALITY_AUDIO, RTK_HOST_PRESET } from "./realtimeKitPresets";
import { LivestreamHostView } from "./LivestreamHostView";
import { LivestreamViewerView } from "./LivestreamViewerView";
import { LivestreamWaitingScreen } from "./LivestreamWaitingScreen";
import { useHostLivestreamActive } from "./useLivestreamStageFeeds";
import { useLivestreamSchedulePhase } from "./useLivestreamSchedulePhase";
import type { LivestreamMeetingExperienceProps } from "./meetingProductTypes";

function LivestreamMeetingInner({
  meetingProductId,
  shopProductIds,
  shopTipping,
  leadContact,
  showLogout,
  loggingOut,
  onLogout,
  meetingStart,
  meetingEnd,
  isHostJoinLink,
  productName,
  productImageUrl,
  baseUrl,
}: Omit<LivestreamMeetingExperienceProps, "authToken">) {
  const { meeting } = useRealtimeKitMeeting();
  const roomState = useRealtimeKitSelector((m) => m.self.roomState);
  const isHost = useRealtimeKitSelector(
    (m) => m.self.presetName === RTK_HOST_PRESET,
  );
  const hasHostParticipant = useRealtimeKitSelector((m) =>
    m.participants.joined
      .toArray()
      .some((participant) => participant.presetName === RTK_HOST_PRESET),
  );
  const schedulePhase = useLivestreamSchedulePhase({
    start: meetingStart,
    end: meetingEnd,
  });
  const hasHostLivestream = useHostLivestreamActive({ meeting });

  if (!meeting || roomState !== "joined") {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-black text-white">
        Connecting...
      </div>
    );
  }

  if (isHost || isHostJoinLink) {
    return (
      <LivestreamHostView
        shopProductIds={shopProductIds}
        shopTipping={shopTipping}
        showLogout={showLogout}
        loggingOut={loggingOut}
        onLogout={onLogout}
        productName={productName}
        baseUrl={baseUrl}
      />
    );
  }

  if (!hasHostLivestream) {
    if (schedulePhase === "ended") {
      return (
        <LivestreamWaitingScreen
          productName={productName ?? "Livestream"}
          imageUrl={productImageUrl}
          pageTitle={productName ? `${productName} · Livestream` : "Livestream"}
          start={meetingStart}
          end={meetingEnd}
          meetingUrl={`/meetings/${meetingProductId}`}
          phase="ended"
        />
      );
    }

    if (
      schedulePhase === "before-start-far" ||
      schedulePhase === "before-start-countdown"
    ) {
      return (
        <LivestreamWaitingScreen
          productName={productName ?? "Livestream"}
          imageUrl={productImageUrl}
          pageTitle={productName ? `${productName} · Livestream` : "Livestream"}
          start={meetingStart}
          end={meetingEnd}
          meetingUrl={`/meetings/${meetingProductId}`}
          phase={schedulePhase}
        />
      );
    }

    if (schedulePhase === "waiting-for-host" && !hasHostParticipant) {
      return (
        <LivestreamWaitingScreen
          productName={productName ?? "Livestream"}
          imageUrl={productImageUrl}
          pageTitle={productName ? `${productName} · Livestream` : "Livestream"}
          start={meetingStart}
          end={meetingEnd}
          meetingUrl={`/meetings/${meetingProductId}`}
          phase="waiting-for-host"
        />
      );
    }
  }

  return (
    <LivestreamViewerView
      meetingProductId={meetingProductId}
      shopProductIds={shopProductIds}
      shopTipping={shopTipping}
      leadContact={leadContact}
      showLogout={showLogout}
      loggingOut={loggingOut}
      onLogout={onLogout}
    />
  );
}

function LivestreamMeetingExperienceInner(props: LivestreamMeetingExperienceProps) {
  const { authToken, ...innerProps } = props;
  const [meetingClient, initMeeting] = useRealtimeKitClient();

  useEffect(() => {
    initMeeting({
      authToken,
      defaults: {
        audio: false,
        video: false,
        mediaConfiguration: {
          audio: RTK_HIGH_QUALITY_AUDIO,
        },
      },
    });
  }, [authToken, initMeeting]);

  useEffect(() => {
    if (!meetingClient) return;
    void meetingClient.join();
  }, [meetingClient]);

  return (
    <RealtimeKitProvider value={meetingClient}>
      <LivestreamMeetingInner {...innerProps} />
    </RealtimeKitProvider>
  );
}

export function LivestreamMeetingExperience(props: LivestreamMeetingExperienceProps) {
  return <LivestreamMeetingExperienceInner {...props} />;
}
